// ── the features registry ────────────────────────────────────────────────────
//
// THE foundational answer to "what does innernet actually do today, and who can
// see it." One row per real, shipped surface — not a roadmap, not a wish list.
//
// This file is the SOURCE OF TRUTH in three directions at once:
//   1. it DRIVES gating — `canSee()` below is what the UI calls, so a feature's
//      access level cannot drift from how it is enforced;
//   2. it SYNCS to Notion (lib/features/notion.ts) so the founders' database is
//      generated from the code rather than hand-maintained;
//   3. it NAMES the beta surface — `access: 'beta'` is the beta-tester module,
//      granted per-person through `feature_access` (migration 066).
//
// Rules for editing:
//   • only add a row when the thing is actually reachable in production;
//   • `gate` must describe how it is REALLY enforced, or the row is a lie;
//   • removing a feature? delete the row — the Notion sync will archive it.

export type FeatureAccess =
  /** every signed-in person */
  | 'everyone'
  /** founders only (admins table OR ADMIN_EMAILS) */
  | 'founders'
  /** founders + anyone explicitly granted this feature in `feature_access` */
  | 'beta'
  /** signed-out visitors too */
  | 'public';

export type FeatureStatus = 'live' | 'beta' | 'dev-only';

/**
 * How a gated row reaches everyone (the checklist and its notes: lib/features/lifecycle).
 *   'press'  the console's launch press opens it. Every gate it has asks canSee() with the
 *            viewer's `gates`, the runtime overlay the press writes (feature_gates, migration 157).
 *   'switch' a line in code or the database opens it (a release gate, an env, a policy); the press can not.
 *   'never'  operator tooling, or a person's own switch: it is not opened to everyone.
 *   'rides'  it has no gate of its own and opens with the row it rides (`rides`).
 * Rows open to everyone (or the public) and live leave it out.
 */
export type FeatureOpens = 'press' | 'switch' | 'never' | 'rides';

/** What the console's overlay may set for a 'press' row: to testers (per-person grants), or to everyone. */
export type FeatureGate = 'beta' | 'everyone';

export type FeatureArea =
  | 'memory'      // the record itself
  | 'dashboard'   // the signed-in OS
  | 'agent'       // netti
  | 'connect'     // getting memory in and out
  | 'platform'    // API, MCP, SDK
  | 'public'      // marketing, docs, company
  | 'operator';   // founder tooling

export interface Feature {
  /** stable id — used by feature_access rows and the Notion sync. Never rename. */
  id: string;
  name: string;
  area: FeatureArea;
  access: FeatureAccess;
  status: FeatureStatus;
  /** where it lives, for a human opening it */
  route?: string;
  summary: string;
  /** how the gate is REALLY enforced in code — keep honest */
  gate?: string;
  /** ISO date it went live, when known */
  since?: string;
  /** the release this shipped in (lib/release/ledger.ts). Lets "what went into
   *  1.2" be derived from the registry rather than hand-maintained twice. */
  version?: string;
  /** how it reaches everyone, for every row that is not open to everyone yet (see FeatureOpens) */
  opens?: FeatureOpens;
  /** the row whose gate this one opens with, when `opens` is 'rides' */
  rides?: string;
  /** the release a gated row is planned to open in (RELEASE_PLANS), until it does. Kept with the Notion
   *  "innernet · versions" database (lib/features/plans): a change made there is brought back here by hand. */
  target?: ReleasePlan;
  /** ISO date the row was opened to everyone, when that came after it shipped (`since`) */
  opened?: string;
}

/**
 * The releases a feature can be planned for (John, 2026-10-25): 1.4 is the version live today; 2.0 is
 * the agent memory launch (internal name, target 2026-11-09) with ask netti, the developers page, labs,
 * the browser extension and shaped by what you keep private. Agent memory is never 1.4.
 */
export const RELEASE_PLANS = {
  '1.4': 'innernet 1.4 · live today',
  '2.0': 'innernet 2.0 · agent memory (internal name, ~2026-11-09)',
} as const;
export type ReleasePlan = keyof typeof RELEASE_PLANS;

/** Ids referenced from code — use these constants, never a bare string. */
export const F = {
  youSpace: 'you-space',
  youLoom: 'you-loom',
  youMirror: 'you-mirror',
  dailyReflection: 'daily-reflection',
  dailyReflections: 'daily-reflections',
  dayDigest: 'day-digest',
  youPrivate: 'you-private',
  youTidy: 'you-tidy',
  privateRing: 'private-ring',
  ringGrants: 'ring-grants',
  ringWrites: 'ring-writes',
  readReceipts: 'read-receipts',
  agents: 'agents',
  agentIdentity: 'agent-identity',
  moveIn: 'move-in',
  privateAbstraction: 'private-abstraction',
  projectStory: 'project-story',
  projectRepo: 'project-repo',
  nettiInbox: 'netti-inbox',
  shareCard: 'share-card',
  ours: 'ours-shared-spaces',
  sharedPages: 'shared-pages',
  shareStudio: 'share-studio',
  skillsSpace: 'skills-space',
  oursWaitlist: 'ours-waitlist',
  releaseLedger: 'release-ledger',
  nettiChat: 'netti-chat',
  nettiDesk: 'netti-desk',
  rituals: 'rituals',
  adminConsole: 'admin-console',
  organismLab: 'organism-lab',
  browserExtension: 'browser-extension',
  desktopApp: 'desktop-app',
  innside: 'innside',
  labs: 'labs',
  notionPages: 'notion-pages',
  gmailLabel: 'gmail-label',
  surfaceSetup: 'surface-setup',
  liveWork: 'live-work',
  vault: 'vault',
} as const;

export const FEATURES: Feature[] = [
  // ── memory ────────────────────────────────────────────────────────────────
  {
    id: 'context-maps',
    name: 'context maps',
    area: 'memory',
    access: 'everyone',
    status: 'live',
    route: '/projects',
    summary:
      'Per-project memory: dimensions (markdown documents) and nodes (structured entities), versioned by commits.',
  },
  {
    id: 'personal-memory',
    name: 'personal memory (the self map)',
    area: 'memory',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    summary:
      'Cross-project facts about the person — 24 dimensions, bi-temporal (phases never overwrite), each fact evidence-bound.',
  },
  {
    id: 'artifacts',
    name: 'artifacts',
    area: 'memory',
    access: 'everyone',
    status: 'live',
    route: '/artifacts',
    summary: 'Living documents innernet re-organises as findings stream in.',
  },
  {
    id: 'netti-fold',
    name: 'netti fold (capture → map)',
    area: 'memory',
    access: 'everyone',
    status: 'live',
    summary:
      'Background pass that folds raw captures into dimensions and nodes. Runs on the pro model tier.',
  },
  {
    id: 'tasks',
    name: 'tasks',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/tasks',
    summary: 'Task list driven from memory and the MCP task tools.',
    gate: 'was a beta list; opened to everyone (data.ts tasksEnabled = true)',
  },

  // ── the you space ─────────────────────────────────────────────────────────
  {
    id: F.youSpace,
    name: 'the you space',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    summary:
      'Your own record: the daily letter, rhythm, the tools reading your memory, growth, and what innernet holds about you.',
    since: '2026-07-24',
  },
  {
    id: F.dailyReflection,
    name: 'daily reflection',
    area: 'agent',
    access: 'everyone',
    status: 'live',
    summary:
      'A short letter plus evidence-bound observations, written overnight from the real record. An observation that cannot cite its rows is dropped.',
    since: '2026-07-24',
  },
  {
    id: F.dailyReflections,
    name: 'daily reflections',
    area: 'agent',
    access: 'beta',
    status: 'beta',
    opens: 'press',
    route: '/dashboard/you',
    target: '2.0',
    summary:
      'The day, sent back by email as the day turns at midnight: what you covered, what you decided, what innernet learned about you and where each fact went (work · personal · private · …), and a minute-long look at the few netti is least sure it filed right. Keep or move, one tap in the day view. Every look is counted: the curve of how well netti files the way you do, and the habits it picks up (turned on, it files that way itself, but only where that keeps a fact closer). The mail carries a fact\'s words only where a connected tool could already read them.',
    gate: 'canSee(F.dailyReflections) in GET/POST /api/you/reflection (404 otherwise) and in lib/reflections/send (no mail); the mail also needs profiles.reflection_mail, which the person turns on. Founders + anyone granted it in feature_access. The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
    since: '2026-10-07',
  },
  {
    id: F.youMirror,
    name: 'the mirror (said vs did)',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    summary:
      'What innernet holds about how you work, beside what the record shows. Held side by side; never judged.',
    since: '2026-07-24',
  },
  {
    id: F.youLoom,
    name: 'mind map — the loom',
    area: 'dashboard',
    access: 'beta',
    status: 'beta',
    opens: 'never',
    route: '/dashboard/you',
    summary:
      'Every dimension of you woven into a radial lattice around netti: distance is freshness, size is weight, threads are real co-occurrence. Hover a cell to light its subgraph.',
    gate: 'canSee(F.youLoom) — founders, plus anyone granted it in feature_access',
    since: '2026-07-25',
  },

  {
    id: F.dayDigest,
    name: 'the day digest',
    area: 'agent',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    summary:
      'Once a day, five minutes before each person’s own midnight, netti reads the day they just had and writes what it meant: a headline, the threads, what was decided and left open, and what it noticed. Every item cites real rows or is dropped. The record half (streaks, the day in numbers, the date strip) is computed, never written.',
    since: '2026-09-26',
  },
  {
    id: F.youPrivate,
    name: 'the private view',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    summary:
      'Everything innernet holds about you in one place, every disclosure class included: facts by dimension (each with where it came from, its locked earlier versions, and an edit that writes a new version), the patterns netti noticed, what changed, what each connected app has recorded, and the raw evidence counts. The one home of every fact and pattern. Read from your own rows only; never sent to a model.',
    since: '2026-09-26',
  },
  {
    id: F.youTidy,
    name: 'what repeats',
    area: 'memory',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    version: '1.4',
    opened: '2026-10-25',
    summary:
      'A tidy pass over facts that were kept more than once before the reconciler stopped new repeats: the same words, the same fact reworded, or one quote filed as two kinds of you, and (159) what netti\'s look reads as one fact in other words. The section is always there, with "ask netti to look": one model read over your facts that also finds the ones about a project rather than you ("not about you"), each moved on a press into its project\'s memory and kept here as history. The private view lists each place with the fact that stays, the ones that would join it and the tellings each brings; one pill, asked twice, joins the places the person keeps chosen. Every telling moves to the fact that stays, the others are closed and kept as history, the strictest class stays, and nothing the person wrote is ever closed or moved. Names are left to who you are.',
    gate: 'part of the system for every signed-in person since 2026-10-25 (John: "what repeats … should just be part of the underlying system"): canSee(F.youTidy) in GET/POST /api/you/tidy, the look and refile routes, and the private view\'s section.',
    since: '2026-10-15',
  },
  {
    id: F.privateRing,
    name: 'the private ring',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you',
    version: '1.4',
    opened: '2026-10-25',
    summary:
      'Who may read what of you: per app (a connection or an api key) a depth (standard: trusted, work, public · personal: + self · private: + private) and whether it asks first; access requests to allow once (30 minutes), always, or not; and the history of every self or private fact an app was sent. The door enforces the depths for everyone (standard by default, exactly the behaviour before); this row gates only the controls.',
    gate: 'open to every signed-in person since 2026-10-25 (John: "bring the private ring to everybody"): canSee(F.privateRing) in GET /api/you/ring, the you page and the connections page. The door held every app to standard depth for everyone before; this opens the controls.',
    since: '2026-09-29',
  },
  {
    id: F.ringGrants,
    name: 'grants that end',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you#ring',
    version: '1.4',
    opened: '2026-10-25',
    summary:
      'When an app may read beyond standard, for how long and about what: 30 minutes, a day, a week or always, optionally narrowed to a few categories of personal memory (how you work · what you like · how you talk · what is on your mind · the people in your life · your body and health). Every grant ends unless the person says always: the usual length is a day when it touches something sensitive, a week otherwise. Answered on a request (the categories it asked about) or given from settings ("let it read more for a while"); each grant shows what is left of it and ends in a tap, and a tighter save or a new client ends them all. The door reads live grants on every call, so an ended one is over by the next request.',
    gate: 'opens with the private ring, which every signed-in person has since 2026-10-25 (canSee(F.privateRing) in /api/you/ring); the gate itself runs once migration 149 is on the database (it is).',
    since: '2026-10-15',
  },
  {
    id: F.ringWrites,
    name: 'what an app may write',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you#ring',
    version: '1.4',
    opened: '2026-10-25',
    summary:
      'Per app, beside what it may read: writes freely (the default, as before), asks first (what it would write into memory, about the person or into a project, or forget, waits as a held write the person keeps or lets go; nothing is written until they keep it, so an AI never approves its own memory), or read only (every write refused). Held writes are sealed under the person\'s key and replayed as the app\'s own call when kept; housekeeping writes (tasks, artifacts, handoffs) still go through. The same gate on the MCP door and every REST twin.',
    gate: 'opens with the private ring, which every signed-in person has since 2026-10-25 (canSee(F.privateRing) in /api/you/ring); the gate itself runs once migration 149 is on the database (it is).',
    since: '2026-10-15',
  },
  {
    id: F.readReceipts,
    name: 'read receipts',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/you#ring',
    version: '1.4',
    opened: '2026-10-25',
    summary:
      'A content-free receipt for every response that read memory to an app: which tool, which projects, pages and personal categories by name, how many facts, how many held back, how many characters, and when. One row per response, written after it, never per fact; kept 90 days. Shown per app as "what it read, and when".',
    gate: 'opens with the private ring, which every signed-in person has since 2026-10-25 (canSee(F.privateRing) in /api/you/ring); the gate itself runs once migration 149 is on the database (it is).',
    since: '2026-10-15',
  },
  {
    id: F.agents,
    name: 'agents',
    area: 'agent',
    access: 'beta',
    status: 'beta',
    opens: 'press',
    route: '/dashboard?panel=agents',
    target: '2.0',
    summary:
      'memory for the agents you keep: each agent has its own contained memory, and what it learns about you waits for your yes',
    gate:
      'canSee(F.agents) in data.ts (agentsEnabled) and every /api/agents route (404 otherwise); founders, plus anyone granted it in feature_access (testers, from 2026-10-19; founders only 2026-10-13 → 10-19) The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
    since: '2026-10-12',
  },
  {
    id: F.agentIdentity,
    name: 'agent identity',
    area: 'agent',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/dashboard/agents?v=identities',
    target: '2.0',
    summary:
      'how you want your agent to talk, answer and behave, and the habits it keeps for you, kept once and handed to every ai that reads your memory. netti notices when you tell any ai how you want it to work and offers the line, under the agent it is for, until you keep it',
    gate:
      'canSee(F.agentIdentity) in DashboardClient (identityOpen: the netti space\'s identity view; settings → agent identity hands a pick there since 2026-10-20) and /api/me/agent-identities (404 otherwise); the notice pass (lib/identity/notice) runs only for whoever can see it (featureOpenFor); the door hands an identity only when a line was kept (2026-10-18) The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
    since: '2026-10-18',
  },
  {
    id: F.privateAbstraction,
    name: 'shaped by what you keep private',
    area: 'agent',
    access: 'founders',
    status: 'beta',
    opens: 'never',
    target: '2.0',
    summary:
      'When an app is not cleared for self or private facts that bear on an ask, netti may read them inside innernet and send innernet_context only a few abstract guidance lines (never a name, a condition, an amount, a date or a phrase of the fact; each line checked in code), ledgered as "abstracted". Off for everyone until the person turns it on.',
    gate: 'lib/self/abstract.ts abstractionSwitch(): an unrevoked feature_access row for this id AND canSee(F.privateAbstraction) (founders only for now), plus INNERNET_RING_ABSTRACT not "off" and migration 129 on the database; the ring decides per app (only withheld tiers, never a denied app, never a shared project).',
    since: '2026-09-29',
  },
  {
    id: F.projectStory,
    name: 'project stories',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/projects',
    summary:
      'Every project told as a story: the weeks are chapters (netti writes them at the week’s end, the record fills in the rest), a 14-day momentum, and the week across your projects, opening into the gradient modal.',
    since: '2026-09-26',
  },
  {
    id: F.projectRepo,
    name: 'the repo view',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/projects',
    summary:
      'A code project reads as its repo: every commit the innernet hook captured, drawn as lanes per branch, each with its reason, its diffstat and the thinking that moved around it (saves, decisions, folds, handoffs within half an hour). A parent shows a grid of its code children; the activity panel gains a repos view. Nothing is fetched from GitHub.',
    since: '2026-09-26',
  },
  {
    id: F.nettiInbox,
    name: 'the netti inbox',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard?panel=activity',
    summary:
      'What netti newly noticed about you, as the folds come in: a new fact, a pattern across your facts, a line you said worth keeping. Each arrival points at its one record on the private page (where it came from, when it was found); private ones arrive veiled and open on a press. Read from pointers only, never a copy of the words.',
    since: '2026-09-26',
  },
  {
    id: F.shareCard,
    name: 'the share card',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    summary:
      'One image of one thing innernet noticed about you (a fact, a pattern, a line you said), set over your own netti: a few styles, standard sizes, the colour and where netti sits, and what to leave out. Copied, sent through the share sheet or saved; drawn in the browser, never uploaded.',
    since: '2026-09-26',
  },

  // ── agent ─────────────────────────────────────────────────────────────────
  {
    id: F.nettiChat,
    name: 'ask netti',
    area: 'agent',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/netti',
    target: '2.0',
    summary:
      'The conversation with netti — it reads and ACTS on your own memory through a tool loop (rename, move, rewrite, park, release a fact).',
    gate: 'data.isAdmin && <NettiDock /> in DashboardClient The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
  },
  {
    id: F.nettiDesk,
    name: 'netti on the desk',
    area: 'agent',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/netti',
    target: '2.0',
    summary:
      "netti in the Mac's bottom-right corner, over whatever app is in front: out of sight at rest; its mark shows when your pointer reaches the very corner, and a click calls it. It brings the ask netti rail, and hands on the Mac that ask before they reach. Set in the netti space, on your mac (inside the Mac app only): the corner and its on/off switch, the sounds, the voice, what it may see and do.",
    gate:
      'canSee(F.nettiDesk) shows the netti space\'s "on your mac" view, inside the Mac app only (DashboardClient, components/os/nettiViews); the hands door /api/netti/hands lets in only isAdminUser || NEXT_PUBLIC_NETTI_CHAT_OPEN, and the corner says it is gated otherwise The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
  },
  {
    id: 'mcp-server',
    name: 'hosted MCP endpoint',
    area: 'platform',
    access: 'everyone',
    status: 'live',
    route: '/api/mcp',
    summary:
      'OAuth 2.1 MCP server — Claude, ChatGPT, Cursor, Cline and others read and write the same memory live.',
  },
  {
    id: 'context-tool',
    name: 'innernet_context (one-door retrieval)',
    area: 'platform',
    access: 'everyone',
    status: 'live',
    summary: 'Budgeted retrieval: spine + focus + periphery for an intent, rather than the whole map.',
  },
  {
    id: F.liveWork,
    name: 'the live river (live work)',
    area: 'platform',
    access: 'founders',
    status: 'beta',
    opens: 'switch',
    summary:
      "Every session working on a project right now, in any tool, on any machine or account: what each says it is doing, the lanes it holds, short notes between sessions, and whole sessions handed over and taken on the person's yes. Claude Code sees it through hooks; every other client on its next innernet call.",
    gate:
      'lib/live/switch.ts liveSwitch(): INNERNET_LIVE not "off", and LIVE_OPEN (lib/release/gates.ts, false) or both an unrevoked feature_access row for this id and canSee(F.liveWork) (founders). Off: innernet_live is absent from tools/list, /api/v1/projects/{slug}/live answers 404, no live field, no piggyback.',
    since: '2026-10-16',
  },

  // ── connect ───────────────────────────────────────────────────────────────
  {
    id: F.moveIn,
    name: 'moving in',
    area: 'connect',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/memory?v=import',
    version: '1.4',
    opened: '2026-10-25',
    summary:
      'bring in everything you already have, from anywhere: files and notes (AGENTS.md, CLAUDE.md, rules files, a notes export), or your connected ai moving its own memory of you over innernet. netti sorts it as it lands: what is about you into your memory, what is about a project into that project (a new one when none fits), how you want your ais to work into your agents\' identities, and what is about someone else kept as theirs. then one sheet shows what moved in',
    gate: 'open to every signed-in person since 2026-10-25 (John: "bring the new import to everybody"): canSee(F.moveIn) is true for all, so the import sheet\'s lanes, the sorting, /api/intake and /api/v1/import answer everyone, and innernet_import is on the public tool list (lib/mcp/registry TOOLS).',
    since: '2026-10-24',
  },
  {
    id: 'import',
    name: 'import your AI history',
    area: 'connect',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/memory?v=import',
    summary: 'Bring Claude / ChatGPT history in and watch it organise into projects and facts.',
  },
  {
    id: 'export',
    name: 'export everything',
    area: 'connect',
    access: 'everyone',
    status: 'live',
    route: '/dashboard/settings',
    summary:
      'One press in settings → data for the whole archive, in an open format: a folder of markdown and json with every map (pages, cells, the commit history, an AGENTS.md and CLAUDE.md), personal memory with whose words each fact rests on, its class, sources and earlier versions, handoffs, tasks and documents. It asks for the password (or an emailed code) first, since it holds every private fact. Also GET /v1/export and innernet_export. It comes back in through import your memory → an innernet archive, or POST /v1/projects and `innernet import dir`.',
    gate: 'none: the button is in settings → data for everyone; the REST and MCP doors give an app only the personal memory the private ring clears it for',
    since: '2026-10-15',
  },
  {
    id: 'connectors',
    name: 'connectors',
    area: 'connect',
    access: 'everyone',
    status: 'live',
    route: '/connectors',
    summary: 'Notion and other external mirrors, plus the per-client connect flows.',
  },
  {
    id: F.browserExtension,
    name: 'browser extension',
    area: 'connect',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/connectors',
    target: '2.0',
    summary:
      'Chrome/Safari capture layer — on-device flag triage, explicit and dwell-gated ambient capture, batched to /v1/memories.',
    gate:
      "canSee(F.browserExtension) — founders get install + pair; everyone else sees the row marked 'soon' and the build download 404s The console's overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).",
  },
  {
    id: F.desktopApp,
    name: 'mac app',
    area: 'connect',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    summary: 'Electron shell: global capture hotkey, local brain, living dock icon.',
    gate: 'canSee(F.desktopApp) — the settings surface is founders-only; the build is still distributed by link The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
  },
  {
    id: F.vault,
    name: 'the vault',
    area: 'connect',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/dashboard/connections?view=vault',
    summary:
      'keys kept sealed inside innernet, yours and your room\'s: what each one is, where it lives, who may open it, when it stops, and a ledger of every opening. a chat can keep a key and describe one but never reads a value; the vault finds keys left in memory as plain words (migration 148).',
    gate: 'canSee(F.vault) in the connections panel (the vault chip), every /api/vault route (404 otherwise), the five innernet_vault_* tools (listed only to founders, refused to anyone else) and /api/v1/vault (404); founders only while it is tested (2026-10-13) The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
    since: '2026-10-13',
  },
  {
    id: 'developer-api',
    name: 'developer platform',
    area: 'platform',
    access: 'everyone',
    status: 'live',
    route: '/dashboard?panel=connections&view=api',
    summary:
      'REST v1 at full parity with the hosted MCP tool surface — every tool has an HTTP twin running the same handlers. Hashed API keys with read/write scopes, a discovery document at /api/v1, and one home in the dashboard (connections → api).',
  },
  {
    id: 'agents-api',
    name: 'the agents api',
    area: 'platform',
    access: 'beta',
    status: 'beta',
    opens: 'rides',
    rides: F.agents,
    route: '/docs/agents-api',
    target: '2.0',
    summary:
      'memory for the agents people build: /api/v1/agents (remember, recall, context, forget a user, export, events), per-agent settings and keys that reach one agent only, signed webhooks, and the funnel that offers what an agent learns about its owner to their personal memory (migration 146).',
    gate: 'agentsRoute (lib/agents/http.ts): a key whose owner can not see F.agents (a founder, or granted `agents` in feature_access) gets a 404, the same answer as a route that does not exist; one grant opens the space and its api together (2026-10-19)',
    since: '2026-10-12',
  },
  {
    id: 'developers-page',
    name: 'innernet for developers',
    area: 'public',
    access: 'public',
    status: 'beta',
    opens: 'switch',
    route: '/developers',
    target: '2.0',
    summary:
      "the developer twin of the homepage, built in rooms the same way: one netti that re-forms per room, agents on rings round its heart, the three doors (npx, mcp, rest) in one switcher, agent memory played turn by turn from the one block to paste, an agent's four spaces, git for thinking in the dashboard's lanes, the six agent verbs with their honest annotations, and pricing for your memory beside your agents'. Unlisted until RELEASE_GATES.developersPublic opens.",
    gate: 'RELEASE_GATES.developersPublic (lib/release/gates.ts): renders on a direct link, noindex, off the menu and the sitemap',
    since: '2026-10-12',
  },

  // ── shared ────────────────────────────────────────────────────────────────
  {
    id: F.ours,
    name: 'ours (shared spaces)',
    area: 'dashboard',
    access: 'founders',
    status: 'beta',
    opens: 'switch',
    route: '/ours',
    summary:
      'A shared innernet: members orbit a space that carries its own palette and its own netti.',
    gate:
      'roomsOpenTo(…) in lib/rooms/open.ts: ROOMS_OPEN (lib/release/gates.ts) or the founder check, closed by default. oursEnabled = roomsOpenGiven(isAdmin) in app/dashboard/data.ts; /ours/[slug] is a 404, joining and beginning a room answer 403 rooms_not_open, and /api/spaces, /api/rooms and /api/ours/people are a 404 (spaceCtx, lib/spaces/server.ts) for anyone else',
  },
  {
    id: 'room-memory',
    name: "a room's memory (settle · netti's proposals)",
    area: 'memory',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    route: '/ours',
    summary:
      "A room keeps a memory of its own, tended by netti from what its members add. An editor can settle a page; when netti would rewrite a settled page it proposes the change instead, for an editor to take or keep. Nobody's hand is kept on either.",
    gate: 'roomsOpenTo (lib/rooms/open.ts), through roomGate (lib/spaces/roomApi.ts): /api/rooms/** answers a non-founder the same 404 a stranger gets. Settling and deciding a proposal also take an editor of the room',
  },
  {
    id: 'room-how-we-think',
    name: 'how the room thinks (the group model)',
    area: 'memory',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    route: '/ours',
    summary:
      "netti's reading of a room, kept on its own page and rebuilt whenever the room's pages move: why it is here, how it decides, what it is betting on, what it decided and why, what is still open (every position kept), where its decisions pull against what it says it values, and the words it uses. Every item quotes the page it rests on and carries a voice set by code (settled · told by several · told once · netti's reading); nothing names anyone. Every AI reading the room, or a project linked into it, gets it beside FOCUS as how_the_room_thinks.",
    gate: 'roomsOpenTo through roomGate (lib/spaces/roomApi.ts) for /api/rooms/[id]/model, and through resolveRoomAccess for the frame innernet_context carries: for a non-founder there is no room, so there is nothing to read',
  },
  {
    id: 'room-since',
    name: 'since you were last here',
    area: 'memory',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    route: '/ours',
    summary:
      'What changed in a room since your own last visit, told by kind and by when and never by who: what others added, and what netti folded. Read from a tool or on the room page; the clock is yours alone.',
    gate: 'roomsOpenTo in the door (resolveRoomAccess, lib/mcp/roomDoor.ts) and in roomGate for /api/rooms/[id]/since: for a non-founder they stand in no room, so there is nothing to be told',
  },
  {
    id: 'room-added',
    name: "what you've added (take back)",
    area: 'memory',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    route: '/ours',
    summary:
      'Everything you added to a room, with what the room did with it (added, kept back, taken back) and why, and a way to take one back while you stand in the room. A kept-back line never stores the words.',
    gate: 'roomsOpenTo in roomGate (/api/rooms/[id]/contributions and its withdraw) and in innernet_rooms (lib/mcp/rooms.ts), which lists nothing and says so to a non-founder',
  },
  {
    id: 'cli-join',
    name: 'innernet join',
    area: 'connect',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    summary:
      'Step into a room from the terminal with an invite, connect your tools, and, inside a repo, be asked once whether this folder offers its commits to the room. The answer is kept on your machine and never in the repo.',
    gate: 'POST /api/v1/rooms/join answers a non-founder 403 rooms_not_open before it reads the invite (lib/rooms/open.ts). The command is hidden from --help and the readme and prints the same sentence in its own words, exiting 0; the docs rows come back on their own when ROOMS_OPEN flips',
  },
  {
    id: 'room-read-across',
    name: 'reading across a linked room',
    area: 'memory',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    summary:
      'A project you have linked into a room is answered from your own map and the room’s together, each section said to be from the room. A project set to never cross, or a room that cannot be read, is answered from your own map alone.',
    gate: 'linkedRoomOf (lib/mcp/handlers.ts) asks roomsOpenTo before it reads a link; for a non-founder, innernet_context answers from their own map alone',
  },
  {
    id: 'never-cross',
    name: 'never let this cross into a room',
    area: 'memory',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    route: '/dashboard',
    summary:
      'One switch on a folder: it is never linked into a room and a room can never read it. Turning it on takes it out of every room it was in, and says how many came out.',
    gate: '/api/os/never-cross answers a non-founder 404 on both verbs (roomsOpenTo), so the folder settings hide the switch. Only the folder’s owner sets it; migration 137 enforces it in the database',
  },
  {
    id: 'share-qr',
    name: 'share + netti QR',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    summary: 'Tokenised share links with a real scannable QR built from the sharer’s own cell.',
  },
  {
    id: F.sharedPages,
    name: 'shared pages (ours › shared)',
    area: 'dashboard',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.ours,
    route: '/dashboard?panel=ours',
    version: '1.2',
    summary:
      'Every door you have opened, in one list — each wearing the ground it opens in, its settings one press away.',
    gate: 'inside the ours panel, which is oursEnabled = roomsOpenGiven(isAdmin) (app/dashboard/data.ts; ROOMS_OPEN or the founder check)',
  },
  {
    id: F.shareStudio,
    name: 'the share studio',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    version: '1.2',
    summary:
      'The full-bleed share surface: the page as the recipient will meet it, live, beside the controls that set it. Pressing `share` anywhere opens it — on a folder, a page, a skill, and, read-only, on a page that is already public.',
    gate: 'none — `share` opens the studio for everyone, everywhere (ShareOptionsBar no longer asks). SkillsSpace still reads canSee(F.shareStudio), which is now true for every signed-in person; the row stays so the surface keeps a name.',
  },
  {
    id: F.skillsSpace,
    name: 'the skills space',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    route: '/dashboard?panel=skills',
    version: '1.2',
    summary:
      'Every page you keep that wears the skill modifier, gathered from all your projects into one space — each one a link you can hand out, and a door to the community index.',
    gate: 'none — a read-only gathering of your own pages (the share STUDIO inside it stays founder-gated)',
  },
  {
    id: F.oursWaitlist,
    name: 'the ours waitlist page',
    area: 'public',
    access: 'founders',
    status: 'beta',
    opens: 'switch',
    route: '/ours',
    version: '1.2',
    summary:
      'The public door for the shared side. Reachable by direct link so it can be reviewed, but unlisted until approved.',
    gate: 'OURS_PUBLIC in lib/release/gates.ts — noindex + out of the menu and sitemap while false',
  },

  // ── public ────────────────────────────────────────────────────────────────
  {
    id: 'docs',
    name: 'docs',
    area: 'public',
    access: 'public',
    status: 'live',
    route: '/docs',
    summary: 'Getting started, MCP wiring per client, schema reference.',
  },
  {
    id: 'company-blog',
    name: 'company + blog',
    area: 'public',
    access: 'public',
    status: 'live',
    route: '/company',
    summary: 'Public letters and product updates.',
  },

  // ── operator ──────────────────────────────────────────────────────────────
  {
    id: F.adminConsole,
    name: 'operator console',
    area: 'operator',
    access: 'founders',
    status: 'live',
    opens: 'never',
    route: '/admin',
    summary: 'Users, cells, economics, emails, activity.',
    gate: 'requireAdmin() — admins table OR ADMIN_EMAILS',
  },
  {
    id: F.releaseLedger,
    name: 'release ledger',
    area: 'operator',
    access: 'founders',
    status: 'live',
    opens: 'never',
    route: '/api/release/sync',
    version: '1.2',
    summary:
      'The code-owned record of what shipped in each version. The public changelog is a projection of it, and a major keyword holds a line back regardless of how it is marked.',
    gate: 'founderOrCron on /api/release/sync; publication is a human act in the CMS',
  },
  {
    id: F.rituals,
    name: 'rituals',
    area: 'operator',
    access: 'founders',
    status: 'live',
    opens: 'never',
    route: '/admin',
    summary:
      'The scheduled server rituals — daily dispatch, pricing pulse, daily reflections, the weekly letter, and the feedback program. Each mailing one carries "send me a test" and a send-to-its-own-audience; the letter also composes without sending.',
    gate: 'the rituals OWNER only (isRitualsOwner — the innernet project owner), not founders. Enforced in /api/rituals, in every run endpoint, and on the chip.',
  },
  {
    id: F.organismLab,
    name: 'netti lab',
    area: 'operator',
    access: 'founders',
    status: 'live',
    opens: 'never',
    summary:
      'The netti tuner at /netti-lab: every person\'s netti on any ground, play mode vs a real hand-tuned override, the mint bands drawn on every dial (and movable, behind a twice-asked guard), a growth trace with an age scrubber, and exports.',
    gate: 'requireAdmin on the page; isAdminUser on every /api/admin/netti-tuner route',
  },
  {
    id: F.innside,
    name: 'innside innernet',
    area: 'operator',
    access: 'founders',
    status: 'beta',
    opens: 'never',
    route: '/innside',
    summary:
      "the founders' internal web of everything innernet does: every surface and where its memories go, every feature, the system map, the code, the memory infrastructure, the research, and what was built latest. indexed at every deploy, searchable, every item linked to what it connects to.",
    gate: "innsideViewer() (lib/innside/gate.ts): signed out sees a quiet door answered 200 with its own card; a signed-in non-founder gets the site's 404; the latest feed reads with the service role only after the gate passes",
    since: '2026-10-14',
  },
  {
    id: F.labs,
    name: 'innernet labs',
    area: 'public',
    access: 'founders',
    status: 'beta',
    opens: 'switch',
    route: '/labs',
    target: '2.0',
    summary:
      "innernet's research on the night: five areas (netti, the resonance model, the infinite window, the person model, the group model), each with its honest state, its evidence and its limits; numbered white papers (01 person models, 02 the infinite window, in draft) and interactive notes whose live pieces scrub with the scroll. content as code (content/labs), no database, no model call.",
    gate: "labsMode() (lib/labs/gate.ts) until RELEASE_GATES.labsPublic opens: signed out sees a quiet door answered 200 with the generic labs card; a signed-in non-founder gets the site's 404; a founder reads. item cards stay generic, /raw answers 404 and the menu, sitemap and llms.txt leave it out while closed",
    since: '2026-10-16',
  },
  {
    id: F.notionPages,
    name: 'notion pages into memory',
    area: 'connect',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/connectors',
    summary:
      'the notion pages a person shares with their integration are brought into one chosen project as captures, newest first, keyed so a page is only kept again when it changes, and folded by netti. runs when the person presses it.',
    gate: 'canSee(F.notionPages) on POST /api/connectors/notion/ingest (404 otherwise) and on the "bring pages in" control in /connectors The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
    since: '2026-10-14',
  },
  {
    id: F.gmailLabel,
    name: 'gmail label into memory',
    area: 'connect',
    access: 'founders',
    status: 'beta',
    opens: 'press',
    route: '/connectors',
    summary:
      'the mail under one gmail label becomes captures in one chosen project, read over imap with an app password sealed under the person\'s own key, keyed by message id. runs when the person presses it.',
    gate: 'canSee(F.gmailLabel) on every /api/connectors/gmail method (404 otherwise) and on the gmail row in /connectors The console\'s overlay can move it to testers or open it to everyone (canSee reads viewer.gates; lib/features/gateStore, feature_gates migration 157).',
    since: '2026-10-14',
  },
  {
    id: F.surfaceSetup,
    name: 'netti sets a surface up',
    area: 'agent',
    access: 'founders',
    status: 'beta',
    opens: 'rides',
    rides: F.nettiChat,
    summary:
      "netti's surface_setup tool: ask netti (or netti on the desk) to connect a tool, install the extension or bring notion or gmail in, and it walks the steps from the surface registry, with the desk's hands where it can.",
    gate: 'rides the ask netti and desk gates (founders); the tool only reads the surface registry, and to anyone who is not a founder it names only live and beta surfaces and ways',
    since: '2026-10-14',
  },
  {
    id: 'feedback',
    name: 'in-app feedback',
    area: 'dashboard',
    access: 'everyone',
    status: 'live',
    summary: 'Report a bug or ask for a feature; lands in Notion and mirrors to usage_events.',
  },
];

export const FEATURE_BY_ID = new Map(FEATURES.map((f) => [f.id, f]));

/**
 * The feature_access row a TEST ACCOUNT holds (lib/testers/accounts; written by lib/testers/settle,
 * service role only, like every grant). It opens every founders row the launch press could open: a
 * row whose `opens` is press, or one that rides such a row. Never a back-office row (`never`), and
 * never a code switch (`switch`: ours, labs, live work) or anything riding one, whose doors are
 * enforced elsewhere (roomsOpenTo …). A test account sees what a person will see once it ships.
 */
export const TEST_ACCOUNT_GRANT = 'test-account';

/**
 * The feature_access row an EARLY BUILDER holds (lib/promises: the letter promises "early access to
 * everything we have not opened to the public yet"). Written by the promise's settle and by the console
 * when a promise is made or "everything early" is granted. It opens what a test account opens, and rides
 * the next release (lib/release/server), without the house account's every-visit re-grant.
 */
export const EARLY_ACCESS_GRANT = 'early-access';

/** the grants that open every row the launch press could open, rather than one row */
export const WHOLE_GRANTS: readonly string[] = [TEST_ACCOUNT_GRANT, EARLY_ACCESS_GRANT];

export const holdsWholeGrant = (grants: readonly string[] | undefined): boolean =>
  !!grants?.some((g) => WHOLE_GRANTS.includes(g));

/**
 * may a grant open this row? Only a row the launch press could open (`opens: 'press'`), or one riding
 * such a row. Back-office rows (`never`) and code switches (`switch`, and what rides one) stay shut to
 * every grant: their doors are enforced elsewhere (requireAdmin, roomsOpenTo …).
 */
export function grantable(featureId: string): boolean {
  const f = FEATURE_BY_ID.get(featureId);
  if (!f) return false;
  if (f.opens === 'press') return true;
  return f.opens === 'rides' && !!f.rides && FEATURE_BY_ID.get(f.rides)?.opens === 'press';
}

/** may a test account (or an early builder) see this founders row? */
export function testerOpens(featureId: string): boolean {
  return FEATURE_BY_ID.get(featureId)?.access === 'founders' && grantable(featureId);
}

/**
 * Is this row granted to this person? Its own feature_access row, or (for a row that rides another,
 * like surface setup on ask netti) a row for the one it rides. Until 2026-10-25 a grant on a founders
 * row opened nothing, so the console and a promise could write "ask netti" for someone and they never
 * saw it (harsh, 10-25); now a grant opens any row the press could open.
 */
function granted(featureId: string, grants: readonly string[] | undefined): boolean {
  if (!grants?.length) return false;
  if (grants.includes(featureId)) return true;
  const rides = FEATURE_BY_ID.get(featureId)?.rides;
  return !!rides && grants.includes(rides);
}

/** Who is looking. `betaFeatures` are ids granted to this person in feature_access. */
export interface Viewer {
  isAdmin?: boolean;
  betaFeatures?: readonly string[];
  /** the console's runtime overlay (feature_gates, migration 157; lib/features/gateStore): a 'press'
   *  row moved to testers or opened to everyone. Read on the server and handed down with the viewer;
   *  absent, every row keeps the access its code gives it. */
  gates?: Readonly<Record<string, FeatureGate>>;
}

const RANK: Record<FeatureAccess, number> = { founders: 0, beta: 1, everyone: 2, public: 3 };

/**
 * The access a row has once the overlay is read. The overlay only ever WIDENS (founders → testers →
 * everyone), only for a row whose `opens` is 'press', and an unknown value is ignored: a stray row in
 * feature_gates can open nothing the console was not built to open, and can close nothing at all.
 */
export function accessFor(featureId: string, gates?: Viewer['gates']): FeatureAccess | null {
  const f = FEATURE_BY_ID.get(featureId);
  if (!f) return null;
  const g = f.opens === 'press' ? gates?.[featureId] : undefined;
  if (g !== 'beta' && g !== 'everyone') return f.access;
  return RANK[g] > RANK[f.access] ? g : f.access;
}

/**
 * THE gate. Every founder-or-beta surface asks this rather than testing isAdmin
 * inline, so the registry and the enforcement can never disagree.
 */
export function canSee(featureId: string, viewer: Viewer): boolean {
  const access = accessFor(featureId, viewer.gates);
  if (!access) return false; // unknown id fails closed
  switch (access) {
    case 'public':
    case 'everyone':
      return true;
    case 'founders':
      if (viewer.isAdmin) return true;
      // a grant (its own row, the row it rides, or a whole grant) opens only what the press could open
      return grantable(featureId) && (holdsWholeGrant(viewer.betaFeatures) || granted(featureId, viewer.betaFeatures));
    case 'beta':
      // founders always see beta; everyone else needs a grant for it (or for the row it rides),
      // or a whole grant where the press could open it (never a person's own switch like the loom)
      if (viewer.isAdmin) return true;
      return granted(featureId, viewer.betaFeatures) || (holdsWholeGrant(viewer.betaFeatures) && grantable(featureId));
    default:
      return false;
  }
}

/** The features a person can actually reach — what the Notion "who sees it" column means. */
export function visibleFeatures(viewer: Viewer): Feature[] {
  return FEATURES.filter((f) => canSee(f.id, viewer));
}

/** Everything currently in the beta module — the surface a beta tester is invited to. */
export const BETA_FEATURES = FEATURES.filter((f) => f.access === 'beta');
